import { useState, useEffect } from "react"
import { Plus, Search, Crown, Users, ShieldCheck, Tag, X } from "lucide-react"
import { useTheme } from "contexts/ThemeContext"
import { getTagChipStyles } from "utils/tagHelpers"
import { Button } from "components/ui/button"
import { Input } from "components/ui/input"
import { UserDetailsModal } from "components/modals/UserDetailsModal"
import { AddUserModal } from "components/modals/AddUserModal"
import { getUsers, getUserTags, deleteUserTag, getUsersStatus } from "services/users"
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card"
import { useUser } from "contexts/UserContext"
import "components/modals/team-responsive.css"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog"

interface UserTagData {
  id: number
  name: string
  color: string
}

interface TeamMember {
  id: number
  name: string
  firstName: string
  lastName: string
  username: string
  email: string
  phone: string
  position: string
  role: string
  workload: number
  tasksCompleted: number
  tasksTotal: number
  lastActivity: string
  isManager: boolean
  isSuperuser: boolean
  isOnline: boolean
  status: "online" | "pausa" | "offline"
  avatarGradient: string
  memberSince: string
  tagNames: string[]
  tags: { id: number; name: string; color: string }[]
  profilePhotoUrl: string | null
}

function TeamMemberRow({ member, onClick, isCurrentUser, theme }: { member: TeamMember; onClick: () => void; isCurrentUser: boolean; theme: 'light' | 'dark' }) {
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <div
      className="team-member-row group px-4 py-3 border-b border-[rgba(106,77,255,0.25)] last:border-b-0 hover:bg-[rgba(106,77,255,0.08)] transition-colors cursor-pointer"
      role="button"
      tabIndex={0}
      aria-label={`Apri profilo di ${member.name}`}
      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onClick() } }}
      style={isCurrentUser ? { border: '0.5px solid rgba(106, 77, 255, 0.2)' } : undefined}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          {member.profilePhotoUrl ? (
            <img src={member.profilePhotoUrl} alt={member.name} className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${member.avatarGradient} flex items-center justify-center`}>
              <span className="text-[var(--hexa-text-primary)] font-semibold text-xs">{getInitials(member.name)}</span>
            </div>
          )}
          <div
            className="absolute bottom-0 right-0 w-2 h-2 rounded-full ring-2 ring-[var(--hexa-surface-elevated)]"
            style={{
              backgroundColor:
                member.status === 'online' ? '#22c55e' :
                member.status === 'pausa' ? '#f59e0b' :
                '#6b7280'
            }}
          />
        </div>

        {/* Info Membro */}
        <div className="flex-1 min-w-0">
          {/* Nome */}
          <div className="flex min-w-0 flex-wrap items-center gap-2 mb-1">
            <span className="min-w-0 break-words [overflow-wrap:anywhere] text-[var(--hexa-text-primary)] font-semibold text-sm">{member.name}</span>
            {isCurrentUser && (
              <span
                style={{ backgroundColor: 'var(--hexa-purple-light)', color: 'var(--hexa-purple)', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 500 }}
              >
                Tu
              </span>
            )}
            {member.isSuperuser && (
              <span className="shrink-0 whitespace-nowrap bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[10px] px-2 py-0.5 rounded-md font-semibold flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                Admin
              </span>
            )}
          </div>

          {/* Ruolo */}
          <div className="flex items-center gap-1.5">
            {member.isManager && <Crown className="w-3 h-3 text-amber-400" />}
            <span className="text-[var(--hexa-text-primary)] text-xs">{member.role}</span>
          </div>

          {/* Tag pills */}
          {member.tags && member.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {member.tags.map(tag => (
                <span
                  key={tag.id}
                  className="inline-flex min-w-0 max-w-full items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium border"
                  style={getTagChipStyles(tag.color, theme)}
                >
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                  <span className="min-w-0 break-words [overflow-wrap:anywhere]">{tag.name}</span>
                </span>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

export default function TeamPageNew() {
  const { user: currentUser } = useUser()
  const { theme } = useTheme()
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const [isLoading, setIsLoading] = useState(true)

  // Tag state
  const [allTags, setAllTags] = useState<UserTagData[]>([])
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null)
  const [tagManageMode, setTagManageMode] = useState(false)
  const [tagToDelete, setTagToDelete] = useState<UserTagData | null>(null)

  useEffect(() => {
    loadTeamData()
    loadTags()
  }, [])

  // Polling stato online ogni 60s
  useEffect(() => {
    const poll = setInterval(() => {
      refreshStatuses()
    }, 60 * 1000)
    return () => clearInterval(poll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadTeamData = async () => {
    setIsLoading(true)
    try {
      const [usersResp, statusResp] = await Promise.all([
        getUsers(),
        getUsersStatus().catch(() => ({ data: [] })),
      ])
      const users = Array.isArray(usersResp.data) ? usersResp.data : []
      const statusList = Array.isArray(statusResp.data) ? statusResp.data : []
      const statusById: Record<number, "online" | "pausa" | "offline"> = {}
      statusList.forEach((s: any) => {
        statusById[s.id] = (s.status as "online" | "pausa" | "offline") || (s.is_online ? "online" : "offline")
      })

      const formattedMembers: TeamMember[] = users.map((user: any, index: number) => {
        const status = statusById[user.id] || "offline"
        return {
          id: user.id,
          name: user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}`
            : user.username || "Utente",
          firstName: user.first_name || "",
          lastName: user.last_name || "",
          username: user.username || "",
          email: user.email || "",
          phone: user.phone || "",
          position: user.position || "",
          role: user.role || "Membro",
          workload: user.workload || 0,
          tasksCompleted: user.tasks_completed || 0,
          tasksTotal: user.tasks_total || 0,
          lastActivity: user.last_activity || "N/A",
          isManager: user.role === 'manager' || user.is_superuser || user.is_manager || false,
          isSuperuser: user.is_superuser || false,
          isOnline: status === "online",
          status,
          avatarGradient: ["from-purple-500 to-blue-500", "from-blue-500 to-cyan-500", "from-orange-500 to-yellow-500"][index % 3],
          memberSince: user.date_joined ? new Date(user.date_joined).toLocaleDateString('it-IT') : "—",
          tagNames: Array.isArray(user.tags) ? user.tags.map((t: any) => t.name) : [],
          tags: Array.isArray(user.tags) ? user.tags : [],
          profilePhotoUrl: user.profile_photo_url || null,
        }
      })

      setMembers(formattedMembers)
    } catch (error) {
      console.error("Error fetching team members:", error)
      setMembers([])
    } finally {
      setIsLoading(false)
    }
  }

  // Aggiorna solo gli stati senza ricaricare l'intera lista
  const refreshStatuses = async () => {
    try {
      const resp = await getUsersStatus()
      const statusList = Array.isArray(resp.data) ? resp.data : []
      const statusById: Record<number, "online" | "pausa" | "offline"> = {}
      statusList.forEach((s: any) => {
        statusById[s.id] = (s.status as "online" | "pausa" | "offline") || (s.is_online ? "online" : "offline")
      })
      setMembers(prev => prev.map(m => {
        const newStatus = statusById[m.id] || "offline"
        if (newStatus === m.status) return m
        return { ...m, status: newStatus, isOnline: newStatus === "online" }
      }))
    } catch (e) {
      console.warn("Status polling failed", e)
    }
  }

  const loadTags = async () => {
    try {
      const response = await getUserTags()
      setAllTags(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error("Error fetching user tags:", error)
    }
  }

  // Tag counts: how many members have each tag
  const tagCounts: Record<string, number> = {}
  allTags.forEach(tag => {
    tagCounts[tag.name] = members.filter(m => m.tagNames.includes(tag.name)).length
  })

  const handleMemberClick = (member: TeamMember) => {
    setSelectedMember(member)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleOpenAddUserModal = () => {
    setIsAddUserModalOpen(true)
  }

  const handleCloseAddUserModal = () => {
    setIsAddUserModalOpen(false)
    loadTeamData()
  }

  const handleMemberUpdated = () => {
    loadTeamData()
  }

  // Keep selectedMember synced with the latest data from the list
  useEffect(() => {
    if (selectedMember) {
      const updated = members.find(m => m.id === selectedMember.id)
      if (updated && updated !== selectedMember) {
        setSelectedMember(updated)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members])

  // Apply filters
  const filteredMembers = members.filter(member => {
    if (searchQuery && !member.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false
    }
if (activeTagFilter && !member.tagNames.includes(activeTagFilter)) {
      return false
    }
    return true
  })

  // Stats
  const totalMembers = members.length
  const managerCount = members.filter(m => m.role === 'manager').length
  const workerCount = members.filter(m => m.role !== 'manager').length
  const onlineCount = members.filter(m => m.status === 'online').length
  const pausaCount = members.filter(m => m.status === 'pausa').length
  const offlineCount = members.filter(m => m.status === 'offline').length

  // Empty state conditions
  const isEmptyDatabase = members.length === 0
  const hasNoResults = filteredMembers.length === 0 && !isEmptyDatabase

  return (
    <div className="team-page flex min-w-0 flex-col h-full pb-24">
      {/* Page Header Section - CTA Area */}
      <div className="w-full max-w-[1280px] mx-auto px-3 sm:px-6 pt-4 pb-3">
        <div className="flex items-center justify-end">
          <Button
            onClick={handleOpenAddUserModal}
            className="bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Aggiungi Utente
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 min-w-0 w-full max-w-[1280px] mx-auto px-3 sm:px-6 pb-4 flex flex-col">
        {/* Search and controls */}
        <div className="flex items-center gap-3 mb-3">
          <div className="relative flex-1 min-w-0 sm:max-w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--hexa-text-muted)]" />
            <Input
              placeholder="Cerca..."
              aria-label="Cerca membri del team"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 pl-10 pr-10 bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] rounded-lg"
            />
            {searchQuery && <button type="button" aria-label="Azzera ricerca team" onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]"><X className="h-4 w-4" /></button>}
          </div>

        </div>

        {/* Stats bar */}
        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 sm:px-5 py-2 mb-3 rounded-lg text-[11px] bg-[var(--hexa-card-bg)] hexa-card-shadow border border-[rgba(106,77,255,0.25)]"
        >
          <span className="font-semibold text-[var(--hexa-text-primary)]">
            TEAM <span className="font-bold">{totalMembers}</span>
          </span>
          <div className="w-px h-4" style={{ backgroundColor: "var(--hexa-border)" }} />
          <span className="text-[var(--hexa-text-secondary)]">
            {managerCount} manager
          </span>
          <div className="w-px h-4" style={{ backgroundColor: "var(--hexa-border)" }} />
          <span className="text-[var(--hexa-text-secondary)]">
            {workerCount} worker
          </span>
        </div>

        {/* Tag filter banner */}
        {activeTagFilter && (
          <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 mb-3 bg-[rgba(123,63,255,0.12)] border border-[rgba(123,63,255,0.3)] rounded-lg text-[11px] text-[var(--hexa-text-secondary)]">
            <span>Filtro tag:</span>
            <span className="inline-flex min-w-0 items-center gap-1.5 font-medium break-words [overflow-wrap:anywhere]">
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: allTags.find(t => t.name === activeTagFilter)?.color || 'var(--hexa-purple)' }}
              />
              <span className="text-[var(--hexa-text-primary)]">{activeTagFilter}</span>
            </span>
            <button
              onClick={() => setActiveTagFilter(null)}
              className="w-4 h-4 rounded flex items-center justify-center bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.2)] text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] transition"
              title="Rimuovi filtro"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}

        {/* Main content - two columns */}
        <div className="flex-1 min-h-0 min-w-0 flex flex-col xl:flex-row gap-4">
          {/* Left column - Member list */}
          <div className="flex-1 min-h-0 min-w-0 flex flex-col">
            {isLoading ? (
              <Card className="bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] hexa-card-shadow">
                <CardContent className="p-8 text-center">
                  <span className="text-[var(--hexa-text-secondary)]">Caricamento...</span>
                </CardContent>
              </Card>
            ) : isEmptyDatabase ? (
              <Card className="bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] hexa-card-shadow">
                <CardContent className="p-8 text-center">
                  <Users className="w-9 h-9 text-[var(--hexa-text-muted)] mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-[var(--hexa-text-primary)] mb-1.5">
                    Nessun membro del team ancora
                  </h3>
                  <p className="text-xs text-[var(--hexa-text-secondary)] mb-4">
                    Inizia aggiungendo il primo utente al team
                  </p>
                  <Button
                    onClick={handleOpenAddUserModal}
                    className="bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    Aggiungi il primo utente
                  </Button>
                </CardContent>
              </Card>
            ) : hasNoResults ? (
              <Card className="bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] hexa-card-shadow">
                <CardContent className="p-8 text-center">
                  <Users className="w-9 h-9 text-[var(--hexa-text-muted)] mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-[var(--hexa-text-primary)] mb-1.5">
                    Nessun membro trovato
                  </h3>
                  <p className="text-xs text-[var(--hexa-text-secondary)]">
                    Prova a modificare i filtri di ricerca per vedere più risultati
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="xl:max-h-[calc(100dvh-265px)] bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] xl:overflow-y-auto scrollbar-hexa hexa-card-shadow">
                {filteredMembers.map(member => (
                  <TeamMemberRow key={member.id} member={member} isCurrentUser={member.id === currentUser?.id} onClick={() => handleMemberClick(member)} theme={theme} />
                ))}
              </Card>
            )}
          </div>

          {/* Right column - Sidebar */}
          <div className="team-insights order-first xl:order-last w-full xl:w-[260px] grid sm:grid-cols-2 xl:flex xl:flex-col gap-4 shrink-0 xl:max-h-[calc(100dvh-265px)]">
            {/* Status card */}
            <Card className="flex-1 min-h-0 overflow-hidden flex flex-col !py-2 !gap-1 bg-[var(--hexa-card-bg)] border border-[rgba(106,77,255,0.25)] hexa-card-shadow">
              <CardHeader className="!px-4 !pb-0 flex flex-row items-center justify-between space-y-0 flex-shrink-0">
                <CardTitle className="text-[10px] font-medium text-[var(--hexa-text-secondary)] uppercase tracking-wide flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  Status
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 overflow-hidden !px-4 !pt-3">
                <div className="flex flex-col">
                  {/* Online */}
                  <div className="rounded-lg p-2.5 flex items-center gap-2.5 border-b border-[rgba(106,77,255,0.25)]" style={{ backgroundColor: 'rgba(34,197,94,0.25)' }}>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#22c55e' }} />
                    <span className="text-xs flex-1 font-medium" style={{ color: '#16a34a' }}>Online</span>
                    <span className="text-sm font-bold text-[var(--hexa-text-primary)]">{onlineCount}</span>
                  </div>
                  {/* In pausa */}
                  <div className="rounded-lg p-2.5 flex items-center gap-2.5 border-b border-[rgba(106,77,255,0.25)]" style={{ backgroundColor: 'rgba(245,158,11,0.25)' }}>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#f59e0b' }} />
                    <span className="text-xs flex-1 font-medium" style={{ color: '#d97706' }}>In pausa</span>
                    <span className="text-sm font-bold text-[var(--hexa-text-primary)]">{pausaCount}</span>
                  </div>
                  {/* Offline */}
                  <div className="rounded-lg p-2.5 flex items-center gap-2.5" style={{ backgroundColor: 'rgba(107,114,128,0.25)' }}>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#6b7280' }} />
                    <span className="text-xs flex-1 font-medium" style={{ color: '#6b7280' }}>Offline</span>
                    <span className="text-sm font-bold text-[var(--hexa-text-primary)]">{offlineCount}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tag card */}
            <Card className="flex-1 min-h-0 overflow-hidden flex flex-col !py-2 !gap-1 bg-[var(--hexa-card-bg)] border border-[rgba(106,77,255,0.25)] hexa-card-shadow">
              <CardHeader className="!px-4 !pb-0 flex flex-row items-center justify-between space-y-0 flex-shrink-0">
                <CardTitle className="text-[10px] font-medium text-[var(--hexa-text-secondary)] uppercase tracking-wide flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" />
                  Tag
                </CardTitle>
                {allTags.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setTagManageMode(prev => !prev)}
                    className="text-[10px] text-[var(--hexa-purple)] hover:text-[var(--hexa-purple-hover)] transition"
                  >
                    {tagManageMode ? "Fine" : "Gestisci"}
                  </button>
                )}
              </CardHeader>
              <CardContent className="flex-1 min-h-0 overflow-hidden !px-0">
                {allTags.length === 0 ? (
                  <div className="text-center py-4 px-6">
                    <p className="text-sm text-[var(--hexa-text-secondary)]">Nessun tag creato</p>
                  </div>
                ) : (
                  <div className="flex flex-wrap content-start items-start gap-2 max-h-40 overflow-y-auto scrollbar-hexa px-4 pb-2">
                    {allTags.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => !tagManageMode && setActiveTagFilter(prev => prev === tag.name ? null : tag.name)}
                        className="inline-flex min-w-0 max-w-full items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition hover:opacity-80"
                        style={{
                          ...getTagChipStyles(tag.color, theme, activeTagFilter === tag.name),
                          boxShadow: activeTagFilter === tag.name ? `0 0 0 2px ${tag.color}30` : 'none'
                        }}
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                        <span className="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere] text-left">{tag.name}</span>
                        {tagCounts[tag.name] ? (
                          <span className="ml-0.5 opacity-70">{tagCounts[tag.name]}</span>
                        ) : null}
                        {tagManageMode && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation()
                              setTagToDelete(tag)
                            }}
                            className="ml-1 hover:opacity-100 opacity-60 cursor-pointer"
                          >
                            <X className="w-3 h-3" />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Modal Dettagli Utente */}
      <UserDetailsModal
        member={selectedMember}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onDeleted={loadTeamData}
        onUpdated={handleMemberUpdated}
        currentUserId={currentUser?.id}
      />

      {/* Modal Aggiungi Utente */}
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={handleCloseAddUserModal}
      />

      {/* Delete Tag Confirmation Dialog */}
      <AlertDialog open={!!tagToDelete} onOpenChange={(open) => !open && setTagToDelete(null)}>
        <AlertDialogContent className="bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--hexa-text-primary)]">Eliminare il tag?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--hexa-text-secondary)]">
              Il tag "{tagToDelete?.name}" verrà rimosso da {tagCounts[tagToDelete?.name || ''] || 0} utenti. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[var(--hexa-button-cancel)] text-[var(--hexa-button-cancel-text)] hover:bg-[var(--hexa-button-cancel-hover)] border-none">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white border-none"
              onClick={async () => {
                if (tagToDelete) {
                  try {
                    await deleteUserTag(tagToDelete.id)
                    setAllTags(prev => prev.filter(t => t.id !== tagToDelete.id))
                    if (activeTagFilter === tagToDelete.name) setActiveTagFilter(null)
                  } catch (error) {
                    console.error("Error deleting tag:", error)
                  }
                  setTagToDelete(null)
                }
              }}
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
